import { useEffect, useRef } from 'react'
import { CLARINET_SHAPES, CLARINET_VIEWBOX } from './clarinet-artwork'

export type ClarinetProps = {
  className?: string
  /**
   * Accessible name. Omit and the drawing is `aria-hidden` — the right choice
   * where it is a watermark behind other content, or where a link already
   * names the thing it sits inside. Supply one where the illustration IS the
   * content, and it becomes an `img` with that name.
   */
  label?: string
  /**
   * Also publish `--clarinet-travel` (-1 at the bottom of the viewport, 0 at
   * the middle, +1 at the top), for a consumer that wants the drawing to keep
   * moving after it has finished drawing. Nothing here consumes it: the
   * stylesheet that positions the drawing decides whether that becomes drift,
   * tilt, both, or nothing.
   */
  parallax?: boolean
}

const ROLE_CLASS = {
  b: 'clarinet-body',
  k: 'clarinet-key',
  m: 'clarinet-mouth',
  l: 'clarinet-line',
} as const

/**
 * The clarinet, drawn on by scroll position.
 *
 * The artwork is Fugue Lab's own (see `clarinet-artwork.ts`); the motion is
 * one number. This component's whole job is to keep `--clarinet-draw` (0..1)
 * in step with how far the drawing has travelled up the viewport, and
 * `clarinet.css` derives everything else from it — each shape's dash offset,
 * its fill fade, and its part's assembly travel.
 *
 * Three deliberate choices:
 *
 * 1. The CSS default is `--clarinet-draw: 1`, so the server render and any
 *    no-JS or reduced-motion visitor gets the finished instrument. The scrub
 *    only ever takes it AWAY from finished, which means there is no state in
 *    which the drawing is missing.
 * 2. Progress is measured from the element's own rect rather than a scroll
 *    offset, so it is correct on first paint (mid-page reload, back-forward
 *    restore, anchor jump) without a separate initialisation path. A drawing
 *    already on screen at load computes as finished and simply stays there;
 *    one below the fold computes as 0 and draws as it rises.
 * 3. The runway has a floor of half a viewport. Derived from the drawing's
 *    own height alone, a 200px card would complete in 170px of scrolling —
 *    faster than the flick that brought it on screen, so nobody would see it.
 *
 * This is deliberately NOT a timed animation. A duration would run on its own
 * clock and finish whether or not the visitor was still looking; tied to
 * scroll it is theirs to drive, in both directions.
 *
 * Colour is NOT set here. The four roles read `--clarinet-*` custom
 * properties which each site maps to its own tokens, so the same drawing is
 * an ebony-and-brass watermark on a client site and a portfolio piece in the
 * studio. Import `@gjl/site-kit/clarinet.css` for the motion and defaults.
 */
export function Clarinet({ className = '', label, parallax = false }: ClarinetProps) {
  const ref = useRef<SVGSVGElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let frame = 0
    const measure = () => {
      frame = 0
      const rect = el.getBoundingClientRect()
      const vh = window.innerHeight || 1

      // Starts when the top edge crosses 96% of the viewport and completes
      // after a further 85% of the drawing's own height — or half a viewport,
      // whichever is longer — has scrolled past.
      const runway = Math.max(rect.height * 0.85, vh * 0.5)
      const p = (vh * 0.96 - rect.top) / runway
      el.style.setProperty('--clarinet-draw', String(Math.min(1, Math.max(0, p))))

      if (parallax) {
        // +1 with the drawing's centre at the top of the viewport, -1 at the
        // bottom, 0 dead centre.
        const centre = (rect.top + rect.height / 2) / vh
        el.style.setProperty('--clarinet-travel', String(Math.min(1, Math.max(-1, 1 - 2 * centre))))
      }
    }
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(measure)
    }

    measure()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [parallax])

  return (
    <svg
      ref={ref}
      viewBox={CLARINET_VIEWBOX}
      className={`clarinet ${className}`}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      focusable="false"
    >
      {CLARINET_SHAPES.map((s, i) => (
        <path
          key={i}
          d={s.d}
          className={ROLE_CLASS[s.r]}
          pathLength={1}
          style={
            {
              '--y': s.y,
              '--py': s.py,
              '--o': s.o,
            } as React.CSSProperties
          }
        />
      ))}
    </svg>
  )
}
